import { DateTime } from "luxon";

// Days of week mapping
const DAYS_OF_WEEK = [
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
  { id: 7, name: "Sunday" },
];

/**
 * Adjusts slot days based on UTC to local time conversion
 * @param {Array} slots - Array of slot objects from API with UTC times
 * @param {Object} options - Configuration options
 * @param {string} options.arbitraryDate - Reference date for calculations (default: "2025-01-13")
 * @param {Array} options.daysOfWeek - Custom days array (default: DAYS_OF_WEEK)
 * @param {boolean} options.keepOriginal - Whether to keep original day info (default: true)
 * @returns {Array} - Adjusted slots with correct dayNameId for local display
 */
export function adjustSlotsForLocalTimezone(slots, options = {}) {
  const {
    arbitraryDate = "2025-01-13", // Monday
    daysOfWeek = DAYS_OF_WEEK,
    keepOriginal = true,
    localTimezone = "Asia/Karachi",
  } = options;

  if (!Array.isArray(slots)) {
    console.error("adjustSlotsForLocalTimezone: slots must be an array");
    return [];
  }

  return slots.map((slot) => {
    try {
      // Parse UTC time with arbitrary date
      const utcStart = DateTime.fromFormat(
        `${arbitraryDate} ${slot.startTime}`,
        "yyyy-MM-dd HH:mm:ss",
        { zone: "utc" }
      );

      const utcDateTime = DateTime.fromISO(`${slot?.date}T${slot?.startTime}`, {
        zone: "utc",
      });
      // Convert to local timezone
      const localDateTime = utcDateTime.setZone(localTimezone);
      const localDate = localDateTime.toISODate();

      if (!utcStart.isValid) {
        console.warn(`Invalid start time for slot:`, slot);
        return slot;
      }

      // Convert to local timezone
      const localStart = utcStart.toLocal();

      // Calculate day shift
      const dayShift = localStart.day - utcStart.day;

      // If day shifted, adjust the dayNameId
      if (dayShift !== 0) {
        // Find original day index
        const originalDayIndex = daysOfWeek.findIndex(
          (d) => d.id === slot.dayNameId
        );

        if (originalDayIndex === -1) {
          console.warn(
            `Day ID ${slot.dayNameId} not found in daysOfWeek array`
          );
          return slot;
        }

        // Calculate new day index (with wraparound)
        const newDayIndex = (originalDayIndex + dayShift + 7) % 7;
        const newDay = daysOfWeek[newDayIndex];

        console.log(
          `Slot adjusted: ${slot.day} (${slot.startTime} UTC) -> ${newDay.name} (local time)`
        );

        // Return adjusted slot
        const adjustedSlot = {
          ...slot,
          dayNameId: newDay.id,
          dayName: newDay.name,
          date: localDate,
        };

        // Optionally keep original day info
        if (keepOriginal) {
          adjustedSlot.originalDayNameId = slot.dayNameId;
          adjustedSlot.originalDay = slot.day;
        }

        return adjustedSlot;
      }

      return slot;
    } catch (error) {
      console.error("Error adjusting slot:", slot, error);
      return slot; // Return original slot on error
    }
  });
}

/**
 * Groups adjusted slots by day
 * @param {Array} weekDays - Array of day objects with id and name
 * @param {Array} adjustedSlots - Adjusted slots from adjustSlotsForLocalTimezone
 * @returns {Array} - Week days with slots grouped correctly
 */
export function groupSlotsByDay(weekDays, adjustedSlots) {
  if (!Array.isArray(weekDays)) {
    console.error("groupSlotsByDay: weekDays must be an array");
    return [];
  }

  if (!Array.isArray(adjustedSlots)) {
    console.error("groupSlotsByDay: adjustedSlots must be an array");
    return [];
  }

  return weekDays.map((day) => ({
    ...day,
    slots: adjustedSlots.filter((slot) => slot.dayNameId === day.id),
  }));
}

/**
 * Converts UTC slots to local timezone and groups them by local date
 * @param {Array} utcWeeksArray - Array of week objects: [{ "2026-01-13": [...], ... }, { "2026-01-20": [...], ... }]
 * @param {string} localTimezone - IANA timezone string (e.g., 'Asia/Karachi')
 * @returns {Array} - Array of week objects with slots grouped by local date
 */
export const convertUTCSlotsToLocal = (
  utcWeeksArray,
  localTimezone = "Asia/Karachi"
) => {
  // Validate input
  if (!Array.isArray(utcWeeksArray) || utcWeeksArray.length === 0) {
    return utcWeeksArray;
  }

  // Step 1: Build a map of which week each date belongs to
  const dateToWeekIndex = new Map();
  utcWeeksArray.forEach((weekObject, weekIndex) => {
    if (weekObject && typeof weekObject === "object") {
      Object.keys(weekObject).forEach((date) => {
        dateToWeekIndex.set(date, weekIndex);
      });
    }
  });

  // Step 2: Collect all converted slots with their target dates
  const allConvertedSlots = [];

  utcWeeksArray.forEach((weekObject, weekIndex) => {
    if (!weekObject || typeof weekObject !== "object") {
      return;
    }

    Object.entries(weekObject).forEach(([utcDate, slots]) => {
      // Skip null or empty slots
      if (!Array.isArray(slots) || slots.length === 0) {
        return;
      }

      // Process each slot
      slots.forEach((slot) => {
        // Parse UTC datetime
        const utcDateTime = DateTime.fromISO(`${slot.date}T${slot.startTime}`, {
          zone: "utc",
        });

        // Convert to local timezone
        const localDateTime = utcDateTime.setZone(localTimezone);
        const localDate = localDateTime.toISODate();

        // Parse end time
        const utcEndDateTime = DateTime.fromISO(
          `${slot.date}T${slot.endTime}`,
          {
            zone: "utc",
          }
        );
        const localEndDateTime = utcEndDateTime.setZone(localTimezone);

        // Create converted slot
        const convertedSlot = {
          ...slot,
          date: localDate,
          startTime: slot?.startTime,
          endTime: slot?.endTime,
          dayName: localDateTime.toFormat("cccc"),
          dayNameId: localDateTime.weekday,
          originalUtcDate: slot.date,
          originalUtcDay: slot?.dayName,
          originalUtcDayId: slot?.dayNameId,
        };

        // Find which week this local date belongs to
        const targetWeekIndex = dateToWeekIndex.get(localDate);

        allConvertedSlots.push({
          slot: convertedSlot,
          targetWeekIndex:
            targetWeekIndex !== undefined ? targetWeekIndex : null,
          localDate: localDate,
        });
      });
    });
  });

  // Step 3: Initialize result with all original dates preserved
  const localWeeksArray = utcWeeksArray.map((weekObject) => {
    if (!weekObject || typeof weekObject !== "object") {
      return weekObject;
    }

    const localWeekSlots = {};
    Object.keys(weekObject).forEach((utcDate) => {
      const slots = weekObject[utcDate];
      if (slots === null || slots === undefined) {
        localWeekSlots[utcDate] = slots;
      } else {
        localWeekSlots[utcDate] = [];
      }
    });
    return localWeekSlots;
  });

  // Step 4: Distribute converted slots to their target weeks
  allConvertedSlots.forEach(({ slot, targetWeekIndex, localDate }) => {
    // Only add slot if we found a target week for this date
    if (targetWeekIndex !== null && localWeeksArray[targetWeekIndex]) {
      if (!localWeeksArray[targetWeekIndex][localDate]) {
        localWeeksArray[targetWeekIndex][localDate] = [];
      }
      localWeeksArray[targetWeekIndex][localDate].push(slot);
    }
  });

  // Step 5: Sort slots within each date by start time
  localWeeksArray.forEach((weekObject) => {
    if (weekObject && typeof weekObject === "object") {
      Object.keys(weekObject).forEach((date) => {
        if (Array.isArray(weekObject[date]) && weekObject[date].length > 0) {
          weekObject[date].sort((a, b) => {
            const timeA = DateTime.fromISO(`${a.date}T${a.startTime}`);
            const timeB = DateTime.fromISO(`${b.date}T${b.startTime}`);
            return timeA.toMillis() - timeB.toMillis();
          });
        }
      });
    }
  });

  return localWeeksArray;
};

/**
 * Converts a single slot from UTC to local timezone
 * @param {Object} slot - Single slot object
 * @param {string} localTimezone - IANA timezone string
 * @returns {Object} - Converted slot
 */
export const convertSingleSlotToLocal = (
  slot,
  localTimezone = "Asia/Karachi"
) => {
  const utcStartDateTime = DateTime.fromISO(`${slot.date}T${slot.startTime}`, {
    zone: "utc",
  });
  const utcEndDateTime = DateTime.fromISO(`${slot.date}T${slot.endTime}`, {
    zone: "utc",
  });

  const localStartDateTime = utcStartDateTime.setZone(localTimezone);
  const localEndDateTime = utcEndDateTime.setZone(localTimezone);

  return {
    ...slot,
    date: localStartDateTime.toISODate(),
    startTime: localStartDateTime.toFormat("HH:mm"),
    endTime: localEndDateTime.toFormat("HH:mm"),
    dayName: localStartDateTime.toFormat("cccc"),
    dayNameId: localStartDateTime.weekday,
    originalUTCDate: slot.date,
    originalUTCStartTime: slot.startTime,
    originalUTCEndTime: slot.endTime,
  };
};

/**
 * Gets user's local timezone
 * @returns {string} - IANA timezone string
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Converts UTC time slots to user's local timezone
 * @param {Array} slots - Array of meeting slots with UTC times
 * @returns {Array} - Array of meeting slots converted to local timezone
 */
export const adjustSlotsTimeForMeetingScreens = (slots) => {
  if (!slots || !Array.isArray(slots)) {
    return [];
  }

  return slots?.map((slot) => {
    const startDateTimeUTC = DateTime.fromISO(
      `${slot.date}T${slot.startTime}`,
      { zone: "utc" }
    );

    const endDateTimeUTC = DateTime.fromISO(`${slot.date}T${slot.endTime}`, {
      zone: "utc",
    });
    const startDateTimeLocal = startDateTimeUTC.toLocal();
    const endDateTimeLocal = endDateTimeUTC.toLocal();

    return {
      ...slot,
      date: startDateTimeLocal.toISODate(), // YYYY-MM-DD format
      startTime: startDateTimeLocal.toFormat("HH:mm:ss"), // HH:mm:ss format
      endTime: endDateTimeLocal.toFormat("HH:mm:ss"),
      // store original UTC values
      originalUTC: {
        date: slot.date,
        startTime: slot.startTime,
        endTime: slot.endTime,
      },
      // Add formatted display strings if needed
      // startDateTime: startDateTimeLocal.toISO(),
      // endDateTime: endDateTimeLocal.toISO(),
      // localTimezone: startDateTimeLocal.zoneName,
    };
  });
};
